import React, {useState} from 'react';
import {StatCard} from './StatCard';
import {DomainCard} from './DomainCard';
import {DomainDetail} from './DomainDetail';
import {useDashboard} from './context';
import {hasStrategyContext, solutionList} from './scoring';
import {headingFor, ALL, visibleServiceAreaCount} from '../../lib/portfolio-nav';
import {plural} from '../../lib/text';
import type {Domain} from './types';

/** The phase a domain has progressed furthest into (fallback when `highestPhase` is absent). */
function highestPhaseOf(domain: Domain, phases: string[]): string | undefined {
  if (domain.highestPhase) return domain.highestPhase;
  for (let i = phases.length - 1; i >= 0; i--) {
    if (domain.jtbds.some(j => j.phase === phases[i])) return phases[i];
  }
  return undefined;
}

/** Portfolio landing: headline stats, service-area cards, phase filter, domain grid. */
export function PortfolioView(): React.ReactElement {
  const {phases, phaseColors, serviceAreas, domains} = useDashboard();
  const [activeArea, setActiveArea] = useState('ALL');
  const [activePhase, setActivePhase] = useState('ALL');
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);

  const filteredDomains = domains.filter(
    d =>
      (activeArea === 'ALL' || d.area === activeArea) &&
      (activePhase === 'ALL' || highestPhaseOf(d, phases) === activePhase)
  );

  const realDomains = domains;
  const allJtbds = realDomains.flatMap(d => d.jtbds.map(j => ({...j, domain: d.name, area: d.area})));
  const phaseJtbdCounts: Record<string, number> = phases.reduce((acc, p) => {
    acc[p] = allJtbds.filter(j => j.phase === p).length;
    return acc;
  }, {} as Record<string, number>);
  const activeSolutions = new Set(realDomains.flatMap(d => d.jtbds.flatMap(j => solutionList(j)))).size;

  // Heading reflects the portfolio in view: the data here is already filtered, so a single portfolio
  // present → its full name; multiple (or none) → "All Portfolios" (FR-009).
  const portfolioKeys = [...new Set(domains.map(d => d.portfolio).filter(Boolean) as string[])];
  const heading = headingFor(portfolioKeys.length === 1 ? portfolioKeys[0] : ALL);
  const serviceAreaCount = visibleServiceAreaCount(domains);

  return (
    <>
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.03em', marginBottom: 4}}>
          {heading}
        </h1>
        <p style={{fontSize: 13, color: '#64748B'}}>Specialized Teams AI Transformation · 2026</p>
      </div>
      <div style={{fontSize: 10, color: '#94A3B8', fontFamily: "'DM Mono', monospace", letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14}}>
        Specialized Teams - AIT Summary
      </div>
      <div style={{display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap'}}>
        <StatCard label="Domains" value={realDomains.length} sub={`across ${plural(serviceAreaCount, 'service area')}`} accent="#3B82F6" />
        <StatCard label="Total JTBDs" value={allJtbds.length} sub="jobs identified" accent="#8B5CF6" />
        <StatCard label="Active Solutions" value={activeSolutions} sub="tools & experiments" accent="#DC2626" />
      </div>
      <div style={{display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap'}}>
        <StatCard label="Explore" value={phaseJtbdCounts['Explore'] || 0} sub="JTBDs" accent={phaseColors.Explore?.text || '#2563EB'} />
        <StatCard label="Refine" value={phaseJtbdCounts['Refine'] || 0} sub="JTBDs" accent={phaseColors.Refine?.text || '#16A34A'} />
        <StatCard label="Transform" value={phaseJtbdCounts['Transform'] || 0} sub="JTBDs" accent={phaseColors.Transform?.text || '#7C3AED'} />
        <StatCard label="Optimize" value={phaseJtbdCounts['Optimize'] || 0} sub="JTBDs" accent={phaseColors.Optimize?.text || '#D97706'} />
      </div>

      <div style={{fontSize: 10, color: '#94A3B8', fontFamily: "'DM Mono', monospace", letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14}}>
        Service Areas
      </div>
      <div style={{display: 'flex', gap: 10, marginBottom: 32, flexWrap: 'wrap'}}>
        {serviceAreas.map(sa => {
          const saDomains = domains.filter(d => d.area === sa.id);
          const saJtbds = saDomains.flatMap(d => d.jtbds);
          const phaseCounts: Record<string, number> = phases.reduce((a, p) => ({...a, [p]: 0}), {});
          saJtbds.forEach(j => {
            if (phaseCounts[j.phase] !== undefined) phaseCounts[j.phase]++;
          });
          const active = activeArea === sa.id;
          const scCount = saJtbds.filter(hasStrategyContext).length;
          const emptyDomains = saDomains.filter(d => d.jtbds.length === 0).length;
          const hasData = saJtbds.length > 0;
          const scPct = saJtbds.length ? Math.round((scCount / saJtbds.length) * 100) : 0;
          return (
            <div
              key={sa.id}
              onClick={() => setActiveArea(active ? 'ALL' : sa.id)}
              style={{
                padding: '10px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                background: active ? sa.color + '15' : '#fff',
                border: `1px solid ${active ? sa.color + '60' : !hasData ? '#FECACA' : '#E2E8F0'}`,
                transition: 'all 0.15s'
              }}
            >
              <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6}}>
                <div style={{width: 8, height: 8, borderRadius: '50%', background: sa.color}} />
                <span style={{fontSize: 12, fontWeight: 700, color: active ? sa.color : '#334155'}}>{sa.id}</span>
                <span style={{fontSize: 10, color: '#94A3B8'}}>{plural(saDomains.length, 'domain')}</span>
              </div>
              {hasData ? (
                <>
                  <div style={{display: 'flex', gap: 3, height: 5, borderRadius: 3, overflow: 'hidden', width: 120, background: '#F1F5F9'}}>
                    {phases.map(
                      p =>
                        phaseCounts[p] > 0 && <div key={p} style={{flex: phaseCounts[p], background: phaseColors[p]?.dot, opacity: 0.8}} />
                    )}
                  </div>
                  <div style={{marginTop: 4, display: 'flex', alignItems: 'center', gap: 6}}>
                    <span style={{fontSize: 10, color: '#94A3B8'}}>{saJtbds.length} JTBDs</span>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        padding: '1px 5px',
                        borderRadius: 3,
                        background: scPct === 100 ? '#F0FDF4' : scPct > 0 ? '#FFFBEB' : '#FEF2F2',
                        color: scPct === 100 ? '#16A34A' : scPct > 0 ? '#D97706' : '#DC2626',
                        border: `1px solid ${scPct === 100 ? '#BBF7D0' : scPct > 0 ? '#FDE68A' : '#FECACA'}`
                      }}
                    >
                      {scPct}% SC
                    </span>
                  </div>
                  {emptyDomains > 0 && (
                    <div style={{marginTop: 3, fontSize: 9, fontWeight: 600, color: '#D97706'}}>
                      ⚠ {emptyDomains} domain{emptyDomains > 1 ? 's' : ''} with no JTBDs
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{marginTop: 2, fontSize: 10, fontWeight: 600, color: '#DC2626'}}>⚠ No data loaded</div>
                  {saDomains.length > 0 && emptyDomains > 0 && (
                    <div style={{fontSize: 9, color: '#94A3B8', marginTop: 2}}>
                      {emptyDomains} empty domain shell{emptyDomains > 1 ? 's' : ''}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
        {activeArea !== 'ALL' && (
          <button
            onClick={() => setActiveArea('ALL')}
            style={{background: '#F1F5F9', border: '1px solid #E2E8F0', color: '#64748B', borderRadius: 8, padding: '6px 14px', fontSize: 11, cursor: 'pointer', fontFamily: "'DM Mono', monospace"}}
          >
            ✕ Clear
          </button>
        )}
      </div>

      <div style={{display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center'}}>
        <span style={{fontSize: 10, color: '#94A3B8', fontFamily: "'DM Mono', monospace", letterSpacing: '0.1em', textTransform: 'uppercase'}}>Phase</span>
        <button
          onClick={() => setActivePhase('ALL')}
          style={{
            background: activePhase === 'ALL' ? '#F1F5F9' : 'none',
            border: '1px solid #E2E8F0',
            color: activePhase === 'ALL' ? '#0F172A' : '#94A3B8',
            borderRadius: 6,
            padding: '4px 12px',
            fontSize: 11,
            cursor: 'pointer',
            fontFamily: "'DM Mono', monospace"
          }}
        >
          All Areas
        </button>
        {phases.map(p => (
          <button
            key={p}
            onClick={() => setActivePhase(activePhase === p ? 'ALL' : p)}
            style={{
              background: activePhase === p ? phaseColors[p]?.pill : 'none',
              border: `1px solid ${activePhase === p ? phaseColors[p]?.border : '#E2E8F0'}`,
              color: activePhase === p ? phaseColors[p]?.text : '#94A3B8',
              borderRadius: 6,
              padding: '4px 12px',
              fontSize: 11,
              cursor: 'pointer',
              fontFamily: "'DM Mono', monospace"
            }}
          >
            {p}
          </button>
        ))}
      </div>

      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12}}>
        {filteredDomains.map(d => (
          <DomainCard key={String(d.id ?? d.name)} domain={d} onClick={setSelectedDomain} />
        ))}
      </div>

      {selectedDomain && <DomainDetail domain={selectedDomain} onClose={() => setSelectedDomain(null)} />}
    </>
  );
}
